import { Component, OnInit } from '@angular/core';
import { PublicationService } from './services/publication.service';
import { Publication } from './models/publication.model';
import {
  ApiDataResponse,
  ApiListResponse,
  PaginationData,
} from '@/shared/models/api-response.interface';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { SpatialUnitGroupService } from '@/core/services/spatial-unit-group.service';
import { QuartierState } from '@/utiles/store/quartier.state';
import { Quartier } from '@/core/interfaces/quartier.interface';

@Component({
  selector: 'app-citizen-publications',
  templateUrl: './citizen-publications.component.html',
  styleUrls: ['./citizen-publications.component.css'],
})
export class CitizenPublicationsComponent implements OnInit {
  publications: Publication[] = [];
  paginationData!: PaginationData;
  currentPage: number = 0;
  pageSize: number = 10;
  state = { loading: false, error: '' };

  keyword!: string;
  private keywordSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Dropdown state
  showSearchDropdown: boolean = false;

  // Selected item for badge display
  selectedItem: { name: string; subtitle: string } | null = null;

  // Quartiers data
  quartiers: Quartier[] = [];
  loadingQuartiers: boolean = false;
  hasMoreQuartiers: boolean = true;
  quartierSearchKeyword: string = '';
  private quartierSearchSubject = new Subject<string>();

  // Date filter model bindings (YYYY-MM-DD)
  filterStartDate: string | null = null;
  filterEndDate: string | null = null;

  // Validation message for date interval
  dateFilterError: string = '';

  constructor(
    private _publicationService: PublicationService,
    private _spatialUnitGroupService: SpatialUnitGroupService,
    private _quartierState: QuartierState
  ) {}

  ngOnInit(): void {
    // Initialize default date filter to the last 15 days
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 15);
    this.filterStartDate = start.toISOString().slice(0, 10);
    this.filterEndDate = end.toISOString().slice(0, 10);

    this.keywordSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.keyword = term.trim();
        // Si le dropdown est ouvert, rechercher les quartiers
        if (this.showSearchDropdown) {
          this.searchQuartiers(term.trim());
        } else {
          // Sinon, rechercher les publications
          this.performSearch(term);
        }
      });

    this.quartierSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.quartierSearchKeyword = term.trim();
        this.searchQuartiers(term.trim());
      });

    this.getPublications(undefined, this.filterStartDate, this.filterEndDate);

    this._quartierState.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.quartiers = state.quartiers;
      this.loadingQuartiers = state.loading;
      this.hasMoreQuartiers = state.hasMore;
    });

    this.loadQuartiers();
  }

  getPublications(
    insaeCode: string | undefined = undefined,
    startDate: string | null = null,
    endDate: string | null = null
  ): void {
    this.state.loading = true;
    this._publicationService
      .getPublications(this.currentPage, this.pageSize, insaeCode ?? null, startDate, endDate)
      .subscribe({
        next: (data: ApiListResponse<Publication>) => {
          const { content, ...paginationData } = data;
          this.publications = content;
          this.paginationData = paginationData;
          this.state.loading = false;
        },
        error: (err) => {
          this.state.loading = false;
          this.state.error = 'Une erreur est survenue lors du chargement des publications.';
        },
      });
  }

  onSearchInput(term: string): void {
    this.keywordSubject.next(term);
  }

  performSearch(term: string): void {
    this.getPublications(term, this.filterStartDate, this.filterEndDate);
  }

  reloadPublications(data: { page: number; perPage: number }) {
    this.currentPage = data.page;
    this.pageSize = data.perPage;
    this.getPublications(undefined, this.filterStartDate, this.filterEndDate);
  }

  loadQuartiers(keyword: string | null = null, reset: boolean = false): void {
    const state = this._quartierState.getState();

    if (state.loading || (!state.hasMore && !reset)) {
      return;
    }

    this._quartierState.setLoading(true);
    const nextPage = reset ? 0 : state.currentPage + (state.quartiers.length > 0 ? 1 : 0);

    this._spatialUnitGroupService
      .getQuartiers(nextPage, 10, keyword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: ApiDataResponse<Quartier>) => {
          if (reset) {
            this._quartierState.reset();
          }
          this._quartierState.setQuartiers(
            data.object.content,
            data.object.pageable.pageNumber,
            data.object.totalPages
          );
        },
        error: (err) => {
          console.error('Error loading quartiers:', err);
          this._quartierState.setLoading(false);
        },
      });
  }

  /** Search quartiers based on keyword */
  searchQuartiers(keyword: string): void {
    this._quartierState.reset();
    this.quartierSearchKeyword = keyword;
    this.loadQuartiers(keyword || null, true);
  }

  /** Handle scroll event in dropdown */
  onDropdownScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 50; // pixels from bottom
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;

    if (atBottom && this.hasMoreQuartiers && !this.loadingQuartiers) {
      this.loadQuartiers(this.quartierSearchKeyword || null, false);
    }
  }

  onSearchFocus(): void {
    this.showSearchDropdown = true;
  }

  /** Hide dropdown on search input blur */
  onSearchBlur(): void {
    setTimeout(() => {
      this.showSearchDropdown = false;
      if (!this.showSearchDropdown) {
        this.quartierSearchKeyword = '';
      }
    }, 200);
  }

  selectItem(quartier: Quartier, event: Event): void {
    event.preventDefault();
    this.selectedItem = {
      name: quartier.name,
      subtitle: `${quartier.arrondissement} - ${quartier.commune}`,
    };
    this.keyword = quartier.name;
    this.showSearchDropdown = false;
    // Rechercher les publications avec le insaeCode du quartier
    this.performSearch(quartier.insaecode);
  }

  removeSelectedItem(event: Event): void {
    event.stopPropagation();
    this.selectedItem = null;
    this.keyword = '';
    this.performSearch('');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
